/* eslint-disable max-lines-per-function */
import { fileValidationSchema } from '@/domain/termine/shared/utils/fileValidationSchema';
import { useState, useRef } from 'react';

interface UseFileUploadOptions {
  maxSize?: number;
  acceptedTypes?: readonly string[];
  onFileSelect?: (file: File | null) => void;
}

const maxFileSize = 2 * 1024 * 1024; // 2MB
const acceptedImageTypes = ['image/jpeg', 'image/png', 'image/gif'] as const;

type FileValidationError = {
  key: string;
  params?: Record<string, string>;
};

export function useFileUpload({
  maxSize = maxFileSize,
  acceptedTypes = acceptedImageTypes,
  onFileSelect,
}: UseFileUploadOptions = {}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [errors, setErrors] = useState<FileValidationError[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fileUploadValidationSchema = fileValidationSchema(
    acceptedTypes,
    maxSize,
  );

  const processFile = (selectedFile: File) => {
    const result = fileUploadValidationSchema.safeParse(selectedFile);

    if (!result.success) {
      setErrors(
        result.error.issues.map((issue) => ({
          key: issue.message,
          params: (issue as { params?: Record<string, string> }).params ?? {},
        })),
      );
      setPreviewUrl(null);
      onFileSelect?.(null);
      return false;
    }

    setErrors([]);

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setPreviewUrl(URL.createObjectURL(selectedFile));
    onFileSelect?.(selectedFile);

    return true;
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) processFile(droppedFile);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const handleRemove = (e?: React.MouseEvent) => {
    e?.stopPropagation();

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setPreviewUrl(null);
    setErrors([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onFileSelect?.(null);
  };

  return {
    previewUrl,
    errors,
    isDragOver,
    fileInputRef,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleClick,
    handleInputChange,
    handleRemove,
  };
}
